import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtRM } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Megaphone, Plus, Sparkles, Loader2, ShieldCheck, FileText, Send, Clock, CheckCircle2, AlertTriangle
} from "lucide-react";

const TPL_STATUS = {
  APPROVED: "bg-emerald-100 text-emerald-700", PENDING_META: "bg-amber-100 text-amber-700",
  DRAFT: "bg-slate-100 text-slate-600", REJECTED: "bg-rose-100 text-rose-700",
};
const CMP_STATUS = {
  SENT: "bg-emerald-100 text-emerald-700", SCHEDULED: "bg-sky-100 text-sky-700", DRAFT: "bg-slate-100 text-slate-600",
};

export default function Broadcast() {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: () => api.get("/templates").then((r) => r.data) });
  const { data: campaigns = [] } = useQuery({ queryKey: ["campaigns"], queryFn: () => api.get("/campaigns").then((r) => r.data) });

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto" data-testid="broadcast-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Megaphone className="h-6 w-6 text-primary" /> Approved Broadcast</h1>
        <p className="text-muted-foreground text-sm mt-1">Kempen patuh polisi dengan template diluluskan Meta & audience opt-in sahaja.</p>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList data-testid="broadcast-tabs">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Kempen</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Template</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <div className="flex justify-end"><CampaignDialog templates={templates} qc={qc} /></div>
          <div className="grid md:grid-cols-2 gap-4">
            {campaigns.map((c) => {
              const roi = c.cost > 0 ? Math.round(((c.revenue - c.cost) / c.cost) * 100) : 0;
              return (
                <div key={c.id} data-testid={`campaign-card-${c.id}`} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-display font-semibold">{c.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.template_name} · {c.segment}</p>
                    </div>
                    <Badge className={`${CMP_STATUS[c.status]} border-0 rounded-full`}>{c.status}</Badge>
                  </div>
                  {c.status === "SENT" ? (
                    <>
                      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                        <Metric label="Sent" value={c.sent} />
                        <Metric label="Read" value={c.read} />
                        <Metric label="Replies" value={c.replies} />
                        <Metric label="Leads" value={c.leads} />
                      </div>
                      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Revenue / ROI</p>
                          <p className="font-semibold text-primary">{fmtRM(c.revenue)} · {roi}%</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Sales</p>
                          <p className="font-semibold">{c.sales}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                      {c.status === "SCHEDULED" ? <Clock className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                      {c.status === "SCHEDULED" ? `Dijadualkan · ${new Date(c.scheduled_at).toLocaleDateString("en-MY")}` : "Draft"}
                      <span className="ml-auto">{c.audience_size} audiens</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4 mt-4">
          <div className="flex justify-end gap-2">
            <AITemplateWriter qc={qc} />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} data-testid={`template-card-${t.id}`} className="rounded-2xl border border-border bg-card p-5 flex flex-col">
                <div className="flex items-center justify-between">
                  <p className="font-display font-semibold text-sm">{t.name}</p>
                  <Badge className={`${TPL_STATUS[t.status]} border-0 rounded-full text-[10px]`}>{t.status.replace("_", " ")}</Badge>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t.category} · {t.language}</span>
                <p className="text-sm text-muted-foreground mt-3 flex-1 leading-relaxed">{t.body}</p>
                {t.buttons?.length > 0 && (
                  <div className="flex gap-1.5 mt-3">
                    {t.buttons.map((b) => <span key={b} className="text-[11px] text-primary border border-primary/30 rounded-full px-2.5 py-1">{b}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div><p className="font-semibold">{value}</p><p className="text-[10px] text-muted-foreground uppercase">{label}</p></div>
);

function CampaignDialog({ templates, qc }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", template_id: "", segment: "All Eligible (Opt-in)", audience_size: 500, mode: "NOW" });

  const save = async () => {
    const tpl = templates.find((t) => t.id === form.template_id);
    if (!form.name || !tpl) { toast.error("Nama & template diperlukan"); return; }
    await api.post("/campaigns", {
      name: form.name, template_id: tpl.id, template_name: tpl.name, segment: form.segment,
      audience_size: Number(form.audience_size),
      status: form.mode === "NOW" ? "SENT" : "SCHEDULED",
      scheduled_at: form.mode === "SCHEDULE" ? new Date(Date.now() + 86400000).toISOString() : null,
    });
    toast.success("Kempen dicipta");
    setOpen(false);
    qc.invalidateQueries({ queryKey: ["campaigns"] });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="broadcast-create-campaign-button" className="rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Kempen Baru</Button>
      </DialogTrigger>
      <DialogContent data-testid="campaign-dialog">
        <DialogHeader><DialogTitle>Cipta Kempen Broadcast</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nama Kempen</Label>
            <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Template Diluluskan</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
              <SelectTrigger data-testid="campaign-template-select" className="mt-1"><SelectValue placeholder="Pilih template" /></SelectTrigger>
              <SelectContent>
                {templates.filter((t) => t.status === "APPROVED").map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Hanya template APPROVED boleh dihantar.</p>
          </div>
          <div><Label className="text-xs">Segmen Audiens</Label>
            <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
              <SelectTrigger data-testid="campaign-segment-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["All Eligible (Opt-in)", "New Leads", "Past Customers", "VIP", "Inactive 18 months", "Product Interest"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Saiz Audiens</Label>
              <Input type="number" data-testid="campaign-audience-input" value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Hantar</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="mt-1" data-testid="campaign-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NOW">Sekarang</SelectItem><SelectItem value="SCHEDULE">Jadualkan</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="rounded-lg bg-accent/50 p-3 text-xs text-accent-foreground flex gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" /> Do-Not-Contact & opt-out dikecualikan automatik. Caj WhatsApp/Meta dikira berasingan.
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="campaign-save-button" className="rounded-full w-full"><Send className="h-4 w-4 mr-1.5" /> {form.mode === "NOW" ? "Hantar Kempen" : "Jadualkan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AITemplateWriter({ qc }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("Mesra");
  const [lang, setLang] = useState("ms");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Masukkan idea promosi"); return; }
    setBusy(true); setResult("");
    try {
      const r = await api.post("/ai/template-writer", { prompt, tone, language: lang });
      setResult(r.data.raw);
      toast.success("Template dijana");
    } catch { toast.error("AI tidak tersedia"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="template-ai-writer-button" className="rounded-full bg-violet-600 hover:bg-violet-700 text-white">
          <Sparkles className="h-4 w-4 mr-1.5" /> AI Template Writer
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="ai-writer-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> AI Template Writer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Idea promosi</Label>
            <Input data-testid="ai-writer-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="cth: Promosi Umrah Disember RM8,990" className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="mt-1" data-testid="ai-writer-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Mesra", "Profesional", "Urgent"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Bahasa</Label>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="mt-1" data-testid="ai-writer-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ms">Bahasa Malaysia</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
              </Select></div>
          </div>
          <Button onClick={generate} disabled={busy} data-testid="ai-writer-generate-button" className="rounded-full w-full">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Jana 3 Versi
          </Button>
          {result && (
            <Textarea data-testid="ai-writer-result" value={result} readOnly className="min-h-[220px] font-mono text-xs whitespace-pre-wrap" />
          )}
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Platform tidak menjamin kelulusan — Meta berhak review, luluskan atau tolak template.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
